import { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { BookOpen, Printer, Search, Lightbulb, X } from "lucide-react";
import { SECTIONS } from "@/lib/manualContent";

const norm = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const text = (s) => norm([s.title, s.intro, ...(s.tips || []), ...s.blocks.flatMap((b) => [b.h, b.text, ...(b.steps || []), ...(b.list || []).flat()])].join(" "));

const Steps = ({ steps }) => (
  <ol className="space-y-2.5 mt-3">
    {steps.map((s, i) => (
      <li key={i} className="flex gap-3 text-[0.9375rem] text-slate-700 leading-relaxed">
        <span className="w-6 h-6 shrink-0 rounded-full bg-sky-500 text-slate-950 text-xs font-bold grid place-items-center mt-0.5 print:border print:border-slate-800">{i + 1}</span>
        <span>{s}</span>
      </li>
    ))}
  </ol>
);

const Defs = ({ list }) => (
  <dl className="mt-3 divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
    {list.map(([k, v]) => (
      <div key={k} className="grid sm:grid-cols-[200px_1fr] gap-1 sm:gap-4 px-4 py-2.5 bg-slate-50/80">
        <dt className="text-sm font-semibold text-sky-700">{k}</dt>
        <dd className="text-sm text-slate-700">{v}</dd>
      </div>
    ))}
  </dl>
);

const Figure = ({ src, cap, narrow, onZoom }) => (
  <figure className={`mt-4 ${narrow ? "max-w-[300px]" : "max-w-3xl"}`}>
    <button onClick={() => onZoom(src, cap)} className="block w-full rounded-xl overflow-hidden border border-slate-200 shadow-2xl hover:border-sky-500/50 transition-colors cursor-zoom-in" data-testid={`manual-img-${src}`}>
      <img src={`/manual/${src}`} alt={cap} loading="lazy" className="w-full h-auto block" />
    </button>
    <figcaption className="text-xs text-slate-500 mt-2 font-mono">▲ {cap}</figcaption>
  </figure>
);

const Section = ({ s, n, onZoom }) => {
  const Icon = Icons[s.icon] || BookOpen;
  return (
    <section id={s.id} className="scroll-mt-6 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 manual-section" data-testid={`manual-section-${s.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 shrink-0 rounded-xl bg-sky-500/15 border border-sky-500/30 grid place-items-center"><Icon className="w-6 h-6 text-sky-600" /></div>
        <div>
          <div className="font-mono text-[0.625rem] tracking-[0.25em] text-amber-600">CAPÍTULO {String(n).padStart(2, "0")}</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{s.title}</h2>
        </div>
      </div>
      <p className="mt-4 text-base text-slate-700 leading-relaxed max-w-3xl">{s.intro}</p>
      <div className="mt-6 space-y-8">
        {s.blocks.map((b) => (
          <div key={b.h}>
            <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2"><span className="w-1.5 h-5 rounded bg-sky-500" />{b.h}</h3>
            {b.text && <p className="mt-2 text-[0.9375rem] text-slate-700 leading-relaxed max-w-3xl">{b.text}</p>}
            {b.steps && <Steps steps={b.steps} />}
            {b.list && <Defs list={b.list} />}
            {b.img && <Figure src={b.img} cap={b.cap} narrow={b.narrow} onZoom={onZoom} />}
          </div>
        ))}
      </div>
      {s.tips?.length > 0 && (
        <div className="mt-6 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-1.5">
          {s.tips.map((t) => <div key={t} className="flex gap-2 text-sm text-amber-800"><Lightbulb className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />{t}</div>)}
        </div>
      )}
    </section>
  );
};

export default function Manual() {
  const [q, setQ] = useState("");
  const [zoom, setZoom] = useState(null);
  const [active, setActive] = useState(SECTIONS[0].id);
  const shown = useMemo(() => SECTIONS.filter((s) => !q.trim() || text(s).includes(norm(q.trim()))), [q]);
  useEffect(() => {
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && setActive(e.target.id)), { rootMargin: "-20% 0px -70% 0px" });
    document.querySelectorAll(".manual-section").forEach((el) => io.observe(el));
    const top = () => window.scrollY < 250 && shown[0] && setActive(shown[0].id);
    window.addEventListener("scroll", top, { passive: true });
    return () => { io.disconnect(); window.removeEventListener("scroll", top); };
  }, [shown]);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  return (
    <div className="p-6 lg:p-10" data-testid="manual-page">
      <header className="relative overflow-hidden rounded-2xl border border-sky-500/20 bg-white p-8 sm:p-12 mb-8">
        <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-sky-500/10 blur-3xl" />
        <img src="/brand/eventpol-logo-512.webp" alt="EventPOL · Gestión de eventos policiales" className="hidden sm:block absolute right-8 top-8 w-32 h-32 rounded-3xl shadow-xl" data-testid="manual-logo" />
        <div className="font-mono text-[0.625rem] tracking-[0.3em] text-sky-600">EVENTPOL · APRENDENDO, CONSULTORA DE FORMACIÓN</div>
        <div className="font-mono text-[0.625rem] tracking-[0.3em] text-slate-500 mt-1">POLICÍA LOCAL · PLAN DE TRÁFICO</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mt-2">Manual de usuario</h1>
        <p className="mt-3 text-slate-700 max-w-2xl text-base">Guía para mandos y administradores: de la creación del evento a la entrega de las órdenes de servicio.</p>
        <div className="mt-6 flex flex-wrap gap-3 no-print">
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en el manual (p. ej. desvío, TIP, PDF)…"
              className="h-11 w-full pl-9 pr-3 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-sky-500" data-testid="manual-search-input" />
          </div>
          <button onClick={() => window.print()} className="h-11 px-4 rounded-lg bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold text-sm flex items-center gap-2 transition-colors" data-testid="manual-print-button">
            <Printer className="w-4 h-4" /> Imprimir / Guardar PDF
          </button>
        </div>
      </header>
      <div className="grid lg:grid-cols-[240px_1fr] gap-8 items-start">
        <nav className="no-print lg:sticky lg:top-6 bg-white border border-slate-200 rounded-2xl p-3" data-testid="manual-toc">
          <div className="font-mono text-[0.625rem] tracking-widest text-slate-500 px-2 py-1.5">ÍNDICE</div>
          {SECTIONS.map((s, i) => {
            const on = shown.includes(s);
            return (
              <a key={s.id} href={`#${s.id}`} data-testid={`manual-toc-${s.id}`}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${!on ? "opacity-30 pointer-events-none" : active === s.id ? "bg-sky-500/15 text-sky-700" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"}`}>
                <span className="font-mono text-[0.625rem] w-5 text-slate-500">{String(i + 1).padStart(2, "0")}</span>{s.title}
              </a>
            );
          })}
        </nav>
        <main className="space-y-8 min-w-0">
          {!shown.length && <div className="text-slate-500 text-sm border border-dashed border-slate-200 rounded-xl p-6" data-testid="manual-no-results">No hay resultados para «{q}».</div>}
          {shown.map((s) => <Section key={s.id} s={s} n={SECTIONS.indexOf(s) + 1} onZoom={(src, cap) => setZoom({ src, cap })} />)}
        </main>
      </div>
      {zoom && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm grid place-items-center p-6 no-print" onClick={() => setZoom(null)} data-testid="manual-zoom">
          <button className="absolute top-4 right-4 text-slate-700 hover:text-slate-900" data-testid="manual-zoom-close"><X className="w-7 h-7" /></button>
          <figure className="max-w-6xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <img src={`/manual/${zoom.src}`} alt={zoom.cap} className="max-h-[85vh] w-auto rounded-xl border border-slate-300" />
            <figcaption className="text-center text-sm text-slate-700 mt-3">{zoom.cap}</figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
